/*
 * Кнопка. Геометрия из дизайн-системы «Изнутри» (.btn, .btn-sm, .btn-icon),
 * состояния — из макета «Оболочка - этап 0», карточка «Кнопки · все состояния».
 * Цвет — только из токенов: кнопка не знает, какая тема на дворе.
 */

.button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  font-family: var(--font-ui);
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  line-height: 1.4;
  padding: 8px 15px;
  border: 1px solid transparent;
  border-radius: var(--radius-md);
  background: none;
  cursor: pointer;
  text-decoration: none;
  transition: background 0.15s, border-color 0.15s, color 0.15s;

  /*
   * ПЕРЕНОС ВМЕСТО NOWRAP — отличие от дизайн-системы, и оно намеренное.
   * В .btn дизайн-системы стоит white-space: nowrap. В макете это безопасно: кнопки
   * стоят в ряду неограниченной ширины. В настоящем кабинете кнопка живёт в колонке
   * 360 px и в диалоге 320 px, а немецкая подпись длиннее русской в полтора раза
   * («Изменить прошедшую запись» → «Vergangenen Termin bearbeiten»). Nowrap в этих
   * местах даёт не «красиво в одну строку», а обрезанную или вылезающую подпись.
   */
  white-space: normal;
  text-wrap: balance;
  /* Немецкие составные слова длиннее любой кнопки; лучше разорвать слово, чем колонку. */
  overflow-wrap: anywhere;
}

/*
 * ОБЛАСТЬ НАЖАТИЯ 48 px НА СЕНСОРНОМ ЭКРАНЕ.
 * Видимый размер кнопки не меняется — иначе рассыпалась бы плотность на 1366×768,
 * ради которой дизайнер и выбирал вариант B. Растёт только область попадания: мастер
 * жмёт кнопку между клиентами, часто мокрыми руками и не глядя.
 * Условие по типу указателя, а не по ширине экрана: планшет на стойке администратора
 * шире телефона, но палец на нём тот же.
 */
@media (pointer: coarse) {
  .button::before {
    content: "";
    position: absolute;
    top: 50%;
    left: 50%;
    translate: -50% -50%;
    width: 100%;
    height: 100%;
    min-width: var(--touch-min-target);
    min-height: var(--touch-min-target);
  }
}

/* ─────────── размеры ─────────── */

.sizeSm {
  font-size: var(--font-size-md);
  padding: 5px 11px;
  border-radius: 7px;
}

/*
 * Размер «под палец» — главная кнопка на телефоне мастера: высота 48, во всю ширину
 * (макет «Мастер 360», кнопка «Записать»). Здесь 48 px не запас, а сам размер.
 */
.sizeTouch {
  min-height: var(--touch-min-target);
  padding: 12px 18px;
  font-size: 14px;
  border-radius: var(--radius-lg);
}

.block {
  display: flex;
  width: 100%;
}

/* ─────────── виды ─────────── */

.primary {
  background: var(--accent);
  color: var(--text-on-accent);
}
.primary:hover:not(:disabled):not([aria-disabled="true"]) {
  background: var(--accent-hover);
}
.primary:active:not(:disabled):not([aria-disabled="true"]) {
  background: var(--accent-hover);
}

.secondary {
  background: var(--bg-card);
  color: var(--text-brand);
  border-color: var(--border-strong);
}
.secondary:hover:not(:disabled):not([aria-disabled="true"]) {
  border-color: var(--text-brand);
}
.secondary:active:not(:disabled):not([aria-disabled="true"]) {
  background: var(--bg-sunken);
}

.danger {
  background: var(--danger);
  color: var(--text-on-danger);
}
.danger:hover:not(:disabled):not([aria-disabled="true"]) {
  background: var(--danger-hover);
}
.danger:active:not(:disabled):not([aria-disabled="true"]) {
  background: var(--danger-hover);
}

.dangerGhost {
  background: transparent;
  color: var(--danger-hover);
}
.dangerGhost:hover:not(:disabled):not([aria-disabled="true"]) {
  background: var(--status-no-show-bg);
}

.ghost {
  background: transparent;
  color: var(--text-secondary);
}
.ghost:hover:not(:disabled):not([aria-disabled="true"]) {
  background: var(--bg-sunken);
  color: var(--text-primary);
}

/* ─────────── состояния ─────────── */

.button:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* Выключено правом: выглядит так же, как выключено вообще, но остаётся в обходе
   клавиатуры — почему, написано в button.tsx. */
.button[aria-disabled="true"] {
  opacity: 0.45;
  cursor: not-allowed;
}

.button[aria-busy="true"] {
  opacity: 0.8;
  cursor: progress;
}

/* Крутилка «Сохраняем…». Цвет не задан: он берётся от текста кнопки, поэтому
   одинаково работает и на sage-заливке, и на белой вторичной кнопке. */
.spinner {
  flex: none;
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: 2px solid color-mix(in srgb, currentColor 40%, transparent);
  border-top-color: currentColor;
  animation: plnr-button-spin 0.7s linear infinite;
}

@keyframes plnr-button-spin {
  to {
    rotate: 360deg;
  }
}

@media (prefers-reduced-motion: reduce) {
  .spinner {
    animation-duration: 2.4s;
  }
}

/* ─────────── кнопка-значок ─────────── */

.icon {
  padding: 6px;
  gap: 0;
  border-radius: 7px;
  background: transparent;
  border-color: transparent;
  color: var(--text-secondary);
}
.icon:hover:not(:disabled):not([aria-disabled="true"]) {
  background: var(--bg-sunken);
  color: var(--text-brand);
}

/* Значок 32×32 с границей — из шапки макета (колокольчик у администратора). */
.iconFramed {
  width: 32px;
  height: 32px;
  padding: 0;
  border-color: var(--border-strong);
  border-radius: var(--radius-md);
  color: var(--text-brand);
}
.iconFramed:hover:not(:disabled):not([aria-disabled="true"]) {
  background: var(--bg-sunken);
}

/* 40×40 — тот же колокольчик на телефоне мастера (макет «Мастер 360»). */
.iconLarge {
  width: 40px;
  height: 40px;
  border-radius: var(--radius-lg);
}

.glyph {
  display: block;
  width: 16px;
  height: 16px;
}
